// Updated BonusPuzzleScoring.js scoring logic

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import VideoSubmissionPuzzle from './VideoSubmissionPuzzle';
import JumpItOrFixItPuzzle from './JumpItOrFixItPuzzle';
import { firestore, functions } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getActiveEventId } from '../utils/activeEvent';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';
import './BonusPuzzleScoring.css';

const getPuzzleByPublicCodeFn = httpsCallable(functions, 'getPuzzleByPublicCode');
const submitBonusScoreFn = httpsCallable(functions, 'submitBonusScore');

// Quiz Interface Component
const QuizInterface = ({ questions, answers, wrongQuestions, onAnswerChange }) => {
  return (
    <div className="quiz-interface">
      <h4 style={{ marginBottom: '20px', textAlign: 'left' }}>Answer the following questions:</h4>
      {questions.map((question, qIndex) => {
        const isWrong = wrongQuestions[qIndex]?.isWrong;
        
        return (
        <div 
          key={qIndex} 
          className={`quiz-question ${isWrong ? 'quiz-question-wrong' : ''}`} 
          style={{ marginBottom: '24px', textAlign: 'left' }}
        >
          <div className="question-text" style={{ marginBottom: '12px', fontWeight: 'bold' }}>
            {qIndex + 1}. {question.question}
            {isWrong && <span className="wrong-indicator"> ✗ Incorrect</span>}
          </div>
          
          {question.type === 'short-answer' && (
            <div className="form-group">
              <input
                type="text"
                value={answers[qIndex] || ''}
                onChange={(e) => onAnswerChange(qIndex, e.target.value)}
                placeholder="Enter your answer"
                required
                className={isWrong ? 'input-wrong' : ''}
              />
            </div>
          )}

          {question.type === 'multiple-choice-single' && (
            <div className="quiz-options">
              {(question.options || []).map((option, oIndex) => (
                <label 
                  key={oIndex} 
                  className="quiz-option-label"
                  style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}
                >
                  <input
                    type="radio"
                    name={`question-${qIndex}`}
                    value={oIndex}
                    checked={answers[qIndex] === oIndex.toString()}
                    onChange={(e) => onAnswerChange(qIndex, e.target.value)}
                    required
                    style={{ marginRight: '8px' }}
                  />
                  {option}
                </label>
              ))}
            </div>
          )}

          {question.type === 'multiple-choice-multiple' && (
            <div className="quiz-options">
              {(question.options || []).map((option, oIndex) => {
                const selectedAnswers = Array.isArray(answers[qIndex]) 
                  ? answers[qIndex] 
                  : answers[qIndex] ? [answers[qIndex]] : [];
                const isChecked = selectedAnswers.includes(oIndex.toString());
                
                return (
                  <label 
                    key={oIndex} 
                    className="quiz-option-label"
                    style={{ display: 'block', marginBottom: '8px', cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      value={oIndex}
                      checked={isChecked}
                      onChange={(e) => {
                        const currentAnswers = Array.isArray(answers[qIndex]) 
                          ? answers[qIndex] 
                          : answers[qIndex] ? [answers[qIndex]] : [];
                        if (e.target.checked) {
                          onAnswerChange(qIndex, [...currentAnswers, oIndex.toString()]);
                        } else {
                          onAnswerChange(qIndex, currentAnswers.filter(a => a !== oIndex.toString()));
                        }
                      }}
                      style={{ marginRight: '8px' }}
                    />
                    {option}
                  </label>
                );
              })}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
};

const BonusPuzzleScoring = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { publicCode: urlCode } = useParams();

  const [publicCode, setPublicCode] = useState(null);
  const [puzzle, setPuzzle] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passcodeInput, setPasscodeInput] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [feedback, setFeedback] = useState('');
  const [feedbackType, setFeedbackType] = useState(''); // 'success' | 'error' | 'info'
  const feedbackRef = useRef(null);
  const [quizAnswers, setQuizAnswers] = useState({}); // For quiz type: { questionIndex: answer }
  const [wrongQuestions, setWrongQuestions] = useState({}); // Track wrong questions: { questionIndex: { isWrong: true, correctAnswers: [...] } }
  const [, setInteractiveSponsors] = useState([]);
  const [featuredSponsor, setFeaturedSponsor] = useState(null);

  // Resolve publicCode from URL (works on direct open/mobile/refresh) or from location.state (in-app nav)
  useEffect(() => {
    let code = urlCode || location.state?.publicCode;

    // Fallback: if no code is present in the URL or navigation state, use the last code we stored
    if (!code) {
      try {
        code = window.sessionStorage.getItem('fm_lastBonusPublicCode') || '';
      } catch {
        code = '';
      }
    }

    if (!code || !code.trim()) {
      setError('Puzzle link missing. Please rescan the QR code to reopen this puzzle.');
      setLoading(false);
    } else {
      setPublicCode(code.trim());
    }
  }, [urlCode, location.state]);

  const fetchDoneRef = useRef(false);
  useEffect(() => {
    if (!publicCode) return;
    let cancelled = false;
    fetchDoneRef.current = false;
    const timeoutMs = 20000;

    const fetchEventAndPuzzle = async () => {
      try {
        // Fetch puzzle via Cloud Function — bonusPuzzles is admin-only in rules,
        // the function strips answer fields before returning to the client.
        const puzzleResult = await getPuzzleByPublicCodeFn({ publicCode });
        if (cancelled) return;
        const puzzleData = puzzleResult.data;
        setPuzzle(puzzleData);

        // Resolve event: use puzzle's eventId so puzzles work even when no event is "active"
        let eventData = null;
        if (puzzleData.eventId) {
          const eventSnap = await getDoc(doc(firestore, 'events', puzzleData.eventId));
          if (cancelled) return;
          if (eventSnap.exists()) eventData = { id: eventSnap.id, ...eventSnap.data() };
        }
        if (!eventData) {
          const id = await getActiveEventId();
          if (!id) throw new Error('No event found for this puzzle.');
          const eventSnap = await getDoc(doc(firestore, 'events', id));
          if (cancelled) return;
          if (!eventSnap.exists()) throw new Error('No event found for this puzzle.');
          eventData = { id: eventSnap.id, ...eventSnap.data() };
        }
        setActiveEvent(eventData);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
      if (!cancelled) {
        fetchDoneRef.current = true;
        setLoading(false);
      }
    };

    const t = setTimeout(() => {
      if (cancelled || fetchDoneRef.current) return;
      setLoading(false);
      setError('Loading timed out. Check your connection and try again.');
    }, timeoutMs);

    fetchEventAndPuzzle();
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [publicCode]);

  useEffect(() => {
    if (!activeEvent) return;
    let cancelled = false;
    (async () => {
      try {
        const merged = await fetchSponsorsMergedForEvent(activeEvent.id);
        if (cancelled) return;
        const list = merged
          .filter((s) => (s.type || '') === 'interactive' && !s.hidden);
        list.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        setInteractiveSponsors(list);
        const bonusEligible = list.filter((s) => !!s.showOnBonusPuzzles);
        if (bonusEligible.length > 0) {
          const idx = Math.floor(Math.random() * bonusEligible.length);
          setFeaturedSponsor(bonusEligible[idx]);
        } else {
          setFeaturedSponsor(null);
        }
      } catch (_) {
        if (!cancelled) setInteractiveSponsors([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeEvent]);

  const showFeedback = (msg, type = 'error') => {
    setFeedback(msg);
    setFeedbackType(type);
    // Scroll feedback into view on next render tick
    setTimeout(() => {
      feedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeEvent || !puzzle) return;

    // Client-side early exits (optimistic UX — server validates these too)
    if (activeEvent.isOpen === false) {
      showFeedback('Bonus puzzle scoring is closed for this event.', 'info');
      return;
    }
    if (puzzle.enabled === false) {
      showFeedback('This puzzle is currently disabled.', 'info');
      return;
    }

    // Client-side completeness check for quiz (UX only, not security)
    if (puzzle.type === 'quiz') {
      const questions = puzzle.questions || [];
      if (questions.length === 0) return showFeedback('This quiz has no questions configured.', 'error');
      for (let i = 0; i < questions.length; i++) {
        const answer = quizAnswers[i];
        if (questions[i].type === 'multiple-choice-multiple') {
          if (!Array.isArray(answer) || answer.length === 0)
            return showFeedback(`Please answer all questions. Question ${i + 1} is missing.`, 'error');
        } else {
          if (answer === undefined || answer === null || answer === '')
            return showFeedback(`Please answer all questions. Question ${i + 1} is missing.`, 'error');
        }
      }
    }

    try {
      const result = await submitBonusScoreFn({
        publicCode,
        passcode: passcodeInput,
        answer: codeInput.trim(),
        quizAnswers,
      });
      const { result: outcome, wrongQuestions: wrongList, points } = result.data;

      if (outcome === 'bad_passcode') {
        return showFeedback('Incorrect passcode. Please try again.', 'error');
      }
      if (outcome === 'event_closed') {
        return showFeedback('Bonus puzzle scoring is closed for this event.', 'info');
      }
      if (outcome === 'puzzle_disabled') {
        return showFeedback('This puzzle is currently disabled.', 'info');
      }
      if (outcome === 'already_scored') {
        return showFeedback('This puzzle has already been scored for your team.', 'info');
      }
      if (outcome === 'wrong_answer') {
        return showFeedback('Incorrect code. Please try again.', 'error');
      }
      if (outcome === 'wrong_quiz') {
        // Convert server array [{index, isWrong, ...correctInfo}] to the keyed object the UI expects
        const wrongQuestionsData = (wrongList || []).reduce((acc, q) => {
          const { index, ...rest } = q;
          return { ...acc, [index]: rest };
        }, {});
        setWrongQuestions(wrongQuestionsData);
        const nums = (wrongList || []).map((q) => q.index + 1);
        const label = nums.length === 1 ? `Question ${nums[0]} is` : `Questions ${nums.join(', ')} are`;
        return showFeedback(`${label} incorrect. Please review and try again.`, 'error');
      }
      if (outcome === 'correct') {
        setWrongQuestions({});
        showFeedback(`Correct! You earned ${points} points.`, 'success');
      }
    } catch (err) {
      showFeedback('Error submitting score: ' + err.message, 'error');
    }
  };

  if (loading) return <p>Loading puzzle...</p>;
  if (error) return <p className="error">{error}</p>;
  if (!puzzle) return null;

  if (puzzle.type === 'videoSubmission') {
    return <VideoSubmissionPuzzle puzzle={puzzle} activeEvent={activeEvent} />;
  }

  if (puzzle.type === 'jumpItOrFixIt') {
    // stationId is passed via route state from RedirectPuzzle (see App.js routes
    // /p/:publicCode/:stationId and /puzzle/:publicCode/:stationId). Fall back
    // to sessionStorage so a page refresh on /puzzle still resolves the station.
    let stationId = location.state?.stationId || null;
    if (!stationId) {
      try {
        stationId = window.sessionStorage.getItem('fm_lastBonusStationId') || null;
      } catch {
        stationId = null;
      }
    }
    return <JumpItOrFixItPuzzle puzzle={puzzle} activeEvent={activeEvent} stationId={stationId} />;
  }

  const eventClosed = activeEvent && activeEvent.isOpen === false;
  const puzzleDisabled = puzzle.enabled === false;
  const scoringBlocked = eventClosed || puzzleDisabled;

  return (
    <div className="bonus-puzzle-scoring">
      <img src="/fresh-meet-logo.png" alt="Fresh Meet Logo" className="fresh-meet-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }} />
      <h2>Puzzle {puzzle.number}</h2>
      <h3>{puzzle.title}</h3>
      <p>{puzzle.tagline}</p>
      {eventClosed && (
        <div className="event-closed-banner">
          <p>Bonus puzzle scoring is closed for this event.</p>
        </div>
      )}
      {puzzleDisabled && (
        <div className="event-closed-banner">
          <p>This puzzle is currently disabled.</p>
        </div>
      )}
      {feedback && (
        <p ref={feedbackRef} className={`feedback feedback-${feedbackType}`}>{feedback}</p>
      )}
      <form onSubmit={handleSubmit} className="scoring-form">
        <fieldset disabled={scoringBlocked} className={scoringBlocked ? 'event-closed-fieldset' : ''}>
        <div className="form-group">
          <label>Team Passcode:</label>
          <input
            type="text"
            value={passcodeInput}
            onChange={(e) => setPasscodeInput(e.target.value)}
            placeholder="e.g. Meeple"
            required
          />
          <small style={{ display: 'block', marginTop: '5px', color: '#64748b', textAlign: 'left' }}>
            Enter your team's passcode to claim your points.
          </small>
        </div>
        
        {puzzle.type === 'code verified' && (
          <div className="form-group">
            <label>Puzzle Code:</label>
            <input
              type="text"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              required
            />
          </div>
        )}

        {puzzle.type === 'quiz' && (
          <QuizInterface
            questions={puzzle.questions || []}
            answers={quizAnswers}
            wrongQuestions={wrongQuestions}
            onAnswerChange={(questionIndex, answer) => {
              setQuizAnswers({ ...quizAnswers, [questionIndex]: answer });
              if (wrongQuestions[questionIndex]) {
                const updated = { ...wrongQuestions };
                delete updated[questionIndex];
                setWrongQuestions(updated);
              }
            }}
          />
        )}
        
        <button type="submit" className="btn-fm-primary">Submit Score</button>
        </fieldset>
      </form>
      {featuredSponsor && featuredSponsor.logo && (
        <div className="bonus-featured-sponsor" aria-label="Bonus sponsor">
          <p className="bonus-featured-label">Bonus sponsor</p>
          <a
            href={(featuredSponsor.website || '').trim() || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="bonus-featured-link"
          >
            <img
              src={featuredSponsor.logo}
              alt={featuredSponsor.name || 'Sponsor'}
              className="bonus-featured-logo"
            />
          </a>
          {featuredSponsor.name && (
            <p className="bonus-featured-name">{featuredSponsor.name}</p>
          )}
        </div>
      )}
      <div className="bonus-return-home-wrap">
        <button type="button" onClick={() => navigate('/')} className="bonus-return-home-btn">
          Return to Main Page
        </button>
      </div>
    </div>
  );
};

export default BonusPuzzleScoring;
